import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { AlertTriangle, CalendarDays, Loader2 } from 'lucide-react';

import { useSaveBoardCalendlyLink } from '../../../services/onboardingBoard';
import { normalizeCalendlyUrl } from './leadUtils';
import useAnchoredPopover from './useAnchoredPopover';

// My Calendly (Nic 2026-09-23): every Sales Rep books meetings on their OWN
// Calendly, and a lead's meeting goes on its owner's calendar (even when
// someone covers). This header pill is where the rep sets it. The dot says
// whether it's set: green yes, amber no (Book follow-up stays off until it
// is). Validation errors render inline, never as a toast.
// The link form on its own (2026-10-20): the pill below and the Sales
// Workspace's Connections panel both use it. onDone runs after a save or
// Cancel.
export const CalendlyLinkForm = ({ myCalendlyUrl = '', apiBase = '/onboarding-board', onDone }) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(myCalendlyUrl);
  const [err, setErr] = useState('');
  const save = useSaveBoardCalendlyLink(undefined, {}, apiBase);
  const doSave = async () => {
    const { url, error: draftError } = normalizeCalendlyUrl(draft);
    if (draftError) {
      setErr(draftError);
      return;
    }
    try {
      await save.mutateAsync({ calendlyUrl: url });
      queryClient.invalidateQueries(['onboarding-board']);
      toast.success(
        url
          ? 'Your Calendly link is saved.'
          : 'Your Calendly link is cleared. Booking is off until you add one.',
      );
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.msg || "The link couldn't be saved. Try again.");
    }
  };
  return (
    <div>
      <p className="mb-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
        Meetings for your leads book on this calendar, including when someone else books them
        for you. In Calendly, open your meeting type, use Copy link, and paste it here.
      </p>
      <input
        type="url"
        value={draft}
        autoFocus
        onChange={(e) => {
          setDraft(e.target.value);
          if (err) setErr('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') doSave();
        }}
        placeholder="calendly.com/your-name/30min"
        className={`w-full rounded-lg border bg-white px-2.5 py-2 text-sm text-slate-700 outline-none focus:outline-none dark:!bg-white/5 dark:text-slate-100 ${
          err
            ? 'border-rose-300 focus:border-rose-400 dark:border-rose-500/50'
            : 'border-slate-200 focus:border-slate-300 dark:border-white/10 dark:focus:border-white/25'
        }`}
      />
      {err ? (
        <p className="mb-0 mt-1.5 flex items-start gap-1.5 text-xs text-rose-600 dark:text-rose-400">
          <AlertTriangle size={13} className="mt-px shrink-0" />
          <span>{err}</span>
        </p>
      ) : null}
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => onDone?.()}
          className="whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 outline-none hover:bg-slate-100 focus:outline-none dark:text-slate-300 dark:hover:!bg-white/10"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={save.isLoading}
          onClick={doSave}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100"
        >
          {save.isLoading ? <Loader2 size={13} className="animate-spin" /> : null}
          {save.isLoading ? 'Checking...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const MyCalendlyControl = ({ myCalendlyUrl = '', apiBase = '/onboarding-board' }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const set = !!myCalendlyUrl;
  // Placed from the button, clamped inside the window (never off an edge).
  const popStyle = useAnchoredPopover(open, btnRef, 352);
  return (
    <div ref={rootRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={
          set
            ? 'Your Calendly is set. Meetings for your leads book on it.'
            : 'Add your Calendly so meetings for your leads book on your calendar.'
        }
        className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-3.5 py-1.5 text-[11px] font-medium text-slate-500 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] outline-none transition-[color] hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-surface-card dark:text-slate-400 dark:ring-1 dark:ring-white/10 dark:hover:text-slate-200"
      >
        <CalendarDays size={13} />
        {/* Icon + status dot on a phone (Sales header, 2026-09-28). */}
        <span className="os-act-label hidden sm:inline">My Calendly</span>
        <span className={`h-1.5 w-1.5 rounded-full ${set ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      </button>
      {open && popStyle ? (
        <div
          style={popStyle}
          className="z-[1040] rounded-2xl bg-white p-4 shadow-[0_8px_30px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/70 dark:!bg-surface-elevated dark:ring-white/10"
        >
          <div className="mb-1 text-sm font-bold text-slate-800 dark:text-slate-100">My Calendly</div>
          <CalendlyLinkForm myCalendlyUrl={myCalendlyUrl} apiBase={apiBase} onDone={() => setOpen(false)} />
        </div>
      ) : null}
    </div>
  );
};

export default MyCalendlyControl;
